import type { Graph, Edge } from '@antv/x6';
import { mergeSameTypeEdges } from './mergeSameTypeEdges';
import { applyInvocationZigzagToEdge, syncInvocationAngleGuides } from './invocationZigzag';

let _suppressFlag = false;

export function isSuppressed(): boolean {
  return _suppressFlag;
}

export function setSuppressed(value: boolean): void {
  _suppressFlag = value;
}

export function scheduleMerge(graph: Graph): void {
  if (!graph) return;
  _suppressFlag = true;
  graph.getEdges().forEach(edge => {
    const edgeData = edge.getData?.() || {};
    if (edgeData._cachedSourceDir || edgeData._cachedTargetDir) {
      edge.setData({ ...edgeData, _cachedSourceDir: undefined, _cachedTargetDir: undefined });
    }
  });
  mergeSameTypeEdges(graph);
  graph.getEdges().forEach(edge => {
    const edgeData = edge.getData?.() || {};
    if (edgeData.edgeSubtype === 'invocation') {
      edge.setData({ ...edgeData, _manualVertices: false });
      applyInvocationZigzagToEdge(edge, graph, true);
    }
  });
  syncInvocationAngleGuides(graph);
  _suppressFlag = false;
}

export class InvocationRefreshScheduler {
  private pendingEdgeIds = new Set<string>();
  private rafId: number | null = null;
  private guideRafId: number | null = null;
  private _guidesHidden = false;

  constructor(private graph: Graph) {}

  flushGuideRefresh(): void {
    if (this._guidesHidden) return;
    if (this.guideRafId !== null) {
      cancelAnimationFrame(this.guideRafId);
      this.guideRafId = null;
    }
    _suppressFlag = true;
    syncInvocationAngleGuides(this.graph);
    _suppressFlag = false;
  }

  scheduleGuideRefresh(): void {
    if (this._guidesHidden) return;
    if (this.guideRafId !== null) return;
    this.guideRafId = requestAnimationFrame(() => {
      this.guideRafId = null;
      this.flushGuideRefresh();
    });
  }

  private flushInvocationBoundaryRefresh = (): void => {
    this.rafId = null;
    if (this.pendingEdgeIds.size) {
      _suppressFlag = true;
      this.pendingEdgeIds.forEach(edgeId => {
        const edge = this.graph.getCellById(edgeId) as any;
        if (!edge?.isEdge?.()) return;
        const edgeData = edge.getData?.() || {};
        if (edgeData.edgeSubtype !== 'invocation') return;
        if (!edgeData._fixedStartManual) {
          edge.setData({ ...edgeData, _fixedStartOffset: undefined, _fixedStartForTargetId: undefined });
        }
        applyInvocationZigzagToEdge(edge, this.graph, true);
      });
      this.pendingEdgeIds.clear();
      _suppressFlag = false;
    }
    if (!this._guidesHidden) {
      this.flushGuideRefresh();
    }
  };

  queueInvocationBoundaryRefresh(edge: Edge): void {
    this.pendingEdgeIds.add(edge.id);
    if (this.rafId !== null) return;
    this.rafId = requestAnimationFrame(this.flushInvocationBoundaryRefresh);
  }

  hideInvocationGuidesDuringMove(hiddenIdsRef: { current: string[] }): void {
    if (hiddenIdsRef.current.length > 0) return;
    this._guidesHidden = true;
    if (this.guideRafId !== null) {
      cancelAnimationFrame(this.guideRafId);
      this.guideRafId = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
      this.pendingEdgeIds.clear();
    }
    const hiddenIds: string[] = [];
    this.graph.getEdges().forEach((edge: any) => {
      const data = edge?.getData?.() || {};
      if (!data._invocationAngleGuide) return;
      if (edge.isVisible?.()) {
        edge.hide?.();
        hiddenIds.push(edge.id);
      }
    });
    hiddenIdsRef.current = hiddenIds;
  }

  restoreInvocationGuidesAfterMove(hiddenIdsRef: { current: string[] }): void {
    const hadHidden = hiddenIdsRef.current.length > 0;
    hiddenIdsRef.current = [];
    this._guidesHidden = false;
    if (!hadHidden) return;
    _suppressFlag = true;
    syncInvocationAngleGuides(this.graph);
    _suppressFlag = false;
  }

  cancel(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.guideRafId !== null) {
      cancelAnimationFrame(this.guideRafId);
    }
  }
}
